import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useAccessManagement } from '../hooks/useAccessManagement.js'
import LoadingSpinner from '../components/LoadingSpinner.jsx'

/**
 * AdminPage - Admin landing page for email links
 * 
 * URL: /admin
 * Purpose: Direct admin access from emails, checks admin privileges and redirects appropriately
 * Used in: Admin notification emails, system alert emails, admin task emails
 */
function AdminPage() {
  const navigate = useNavigate()
  const { user } = useCloudContext()
  const { isSystemAdmin, isSuperAdmin, checkSystemAccess } = useAccessManagement()
  
  const [state, setState] = useState({
    loading: true,
    error: null,
    adminLevel: null,
    redirecting: false,
  })

  // Check admin access
  useEffect(() => {
    if (!user) return

    const checkAdminAccess = async () => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }))
        
        // Check if user has any admin privileges
        const hasSystemAccess = await checkSystemAccess(user.uid)
        const systemAdmin = isSystemAdmin(user.uid)
        const superAdmin = isSuperAdmin(user.uid)
        
        if (!hasSystemAccess && !systemAdmin && !superAdmin) {
          setState(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'You do not have administrator privileges.',
          }))
          return
        }
        
        // Determine admin level
        let adminLevel = 'user'
        if (superAdmin) {
          adminLevel = 'super'
        } else if (systemAdmin) {
          adminLevel = 'system'
        } else if (hasSystemAccess) {
          adminLevel = 'support'
        }
        
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          adminLevel,
        }))
        
        // Redirect to appropriate admin area
        setState(prev => ({ ...prev, redirecting: true }))
        
        setTimeout(() => {
          // Navigate to the most appropriate admin destination
          if (adminLevel === 'super' || adminLevel === 'system') {
            navigate('/configuration', { 
              replace: true,
              state: { 
                tab: 'admin',
                message: 'Welcome to the admin area.',
              },
            })
          } else {
            // For support users, go to device management
            navigate('/configuration', { 
              replace: true,
              state: { 
                tab: 'devices',
                message: 'Welcome to device management.',
              },
            })
          }
        }, 1500)
        
      } catch (error) {
        console.error('Error checking admin access:', error)
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Failed to verify admin privileges. Please try again.',
        }))
      }
    }

    checkAdminAccess()
  }, [user, isSystemAdmin, isSuperAdmin, checkSystemAccess, navigate])

  const handleGoToConfiguration = useCallback(() => {
    navigate('/configuration', { replace: true })
  }, [navigate])

  const handleGoToDashboard = useCallback(() => {
    navigate('/dashboard', { replace: true })
  }, [navigate])

  const handleRequestAccess = useCallback(() => {
    // Navigate to support or contact form
    navigate('/support', { 
      state: { 
        requestType: 'admin-access',
        message: 'I need administrator access to the system.',
      },
    })
  }, [navigate])

  // Loading state
  if (state.loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <div className="mt-3">
            <h5 className="mb-1">Verifying Admin Access...</h5>
            <p className="text-muted">Checking your privileges</p>
          </div>
        </div>
      </div>
    )
  }

  // Redirecting state (success)
  if (state.redirecting && !state.error) {
    const getAdminLevelInfo = () => {
      switch (state.adminLevel) {
        case 'super':
          return {
            title: 'Super Administrator',
            description: 'Full system access and control',
            icon: 'bi-shield-fill-check text-danger',
          }
        case 'system':
          return {
            title: 'System Administrator',
            description: 'System management and configuration',
            icon: 'bi-gear-fill text-warning',
          }
        case 'support':
          return {
            title: 'Support Access',
            description: 'Device management and user support',
            icon: 'bi-headset text-info',
          }
        default:
          return {
            title: 'Administrator',
            description: 'Administrative access',
            icon: 'bi-person-badge text-primary',
          }
      }
    }
    
    const adminInfo = getAdminLevelInfo()
    
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className={`bi ${adminInfo.icon}`} style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">Admin Access Confirmed</h4>
                  <p className="text-muted mb-4">
                    Welcome back! Redirecting to admin area...
                  </p>
                  
                  <div className="bg-light rounded p-3 mb-4">
                    <div className="fw-semibold">{adminInfo.title}</div>
                    <small className="text-muted">{adminInfo.description}</small>
                  </div>
                  
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Redirecting...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (state.error) {
    const isAccessDenied = state.error.includes('do not have administrator')
    
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className={`bi ${isAccessDenied ? 'bi-shield-x' : 'bi-exclamation-triangle'} ${isAccessDenied ? 'text-warning' : 'text-danger'}`} style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">
                    {isAccessDenied ? 'Admin Access Required' : 'Admin Access Problem'}
                  </h4>
                  <p className="text-muted mb-4">{state.error}</p>
                  
                  {/* User Info */}
                  {user && (
                    <div className="bg-light rounded p-3 mb-4">
                      <div className="fw-semibold">{user.displayName || user.email}</div>
                      <small className="text-muted">{user.email}</small>
                    </div>
                  )}
                  
                  <div className="d-grid gap-2">
                    {isAccessDenied ? (
                      <button 
                        className="btn btn-primary"
                        onClick={handleRequestAccess}
                      >
                        <i className="bi bi-envelope me-2"></i>
                        Request Admin Access
                      </button>
                    ) : null}
                    
                    <button 
                      className="btn btn-outline-primary"
                      onClick={handleGoToConfiguration}
                    >
                      <i className="bi bi-gear me-2"></i>
                      Go to Configuration
                    </button>
                    
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={handleGoToDashboard}
                    >
                      <i className="bi bi-house me-2"></i>
                      Go to Dashboard
                    </button>
                    
                    {!isAccessDenied && (
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => window.location.reload()}
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Try Again
                      </button>
                    )}
                  </div>
                  
                  {isAccessDenied && (
                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Administrator privileges are required to access this area. 
                        <a href="mailto:admin@revatek.com" className="text-decoration-none ms-1">Contact system admin</a> for assistance.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // This shouldn't normally be reached
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="text-center">
        <h5>Unexpected state</h5>
        <button className="btn btn-primary mt-3" onClick={handleGoToDashboard}>
          Go to Dashboard
        </button>
      </div>
    </div>
  )
}

export default AdminPage
